import { useEffect, useRef, useState } from 'react';
import { NavLink, Outlet, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { flags } from '@cowoo/shared';
import { Wordmark } from '@cowoo/shared/ui-web';
import { env } from '../../env';
import { useAdminStore } from '../../lib/store';
import { AvatarMenu } from './AvatarMenu';
import { useNav } from './useNav';
import { Toaster } from '../../ui/toast';

/**
 * The backoffice shell (10-web-admin § 3, AF-11).
 *
 * The design has two shells — a top navigation and a separate "Zahlen" sidebar
 * with a one-way link back. They are merged into one here, so "Zahlen" is a tab
 * like any other and there is no screen you can only leave by going backwards.
 *
 * Capabilities decide visibility, never `role`: the matrix of `06-auth` § 4.2
 * is the server's, and a UI that re-derives it from the role name drifts from
 * it. What the role cannot open is hidden; single actions inside a screen it
 * *can* open are disabled with a reason instead (§ 2.4).
 */
export function Shell() {
  const { t } = useTranslation();
  const can = useAdminStore((state) => state.can);
  const nav = useNav();

  const items = [
    { to: '/grundrisse', label: t('admin.nav.plans'), capability: 'plans.edit' },
    { to: '/buchungen', label: t('admin.nav.bookings'), capability: 'bookings.manage' },
    { to: '/mitglieder', label: t('admin.nav.members'), capability: 'members.manage' },
    {
      to: '/anfragen',
      label: t('admin.nav.enquiries'),
      capability: 'enquiries.manage',
      count: nav?.enquiriesNew,
    },
    // § 3 — "Finanzen ▾" is a dropdown, not a page. Verträge is owner-only, so
    // for staff the menu holds one entry; it stays a menu anyway, because the
    // same click lands in the same place for both roles.
    {
      to: '/rechnungen',
      label: t('admin.nav.finance'),
      capability: 'bookings.manage',
      menu: [
        { to: '/rechnungen', label: t('admin.nav.invoices'), capability: 'bookings.manage' },
        { to: '/vertraege', label: t('admin.nav.contracts'), capability: 'contracts.office' },
        ...(flags.teams ? [{ to: '/teams', label: t('admin.nav.teams'), capability: 'members.manage' }] : []),
      ].filter((entry) => can(entry.capability)),
    },
    // § 2.4 — staff may open the door, sync and identify, so they see the
    // screen; enrol, config and retire are `terminals.manage` inside it.
    {
      to: '/terminals',
      label: t('admin.nav.terminals'),
      capability: 'door.open',
      dot: nav ? (nav.terminalsOffline > 0 ? ('warn' as const) : ('ok' as const)) : undefined,
    },
    ...(flags.analytics
      ? [{ to: '/zahlen', label: t('admin.nav.analytics'), capability: 'analytics.read' }]
      : []),
    { to: '/einstellungen', label: t('admin.nav.settings'), capability: 'settings.edit' },
  ].filter((item) => can(item.capability));

  return (
    <div className="bg-fog min-h-screen">
      {/* 03 § 2 — the backoffice is desktop only; below 1280 px it says so. */}
      <div className="flex min-h-screen flex-col items-center justify-center px-5 text-center xl:hidden">
        <p className="text-webH3 text-ink max-w-md">{t('admin.shell.tooSmall')}</p>
      </div>

      <div className="hidden xl:block">
        <header className="border-line sticky top-0 z-40 border-b bg-white">
          <nav className="flex h-16 items-center justify-between px-6">
            <div className="flex items-center gap-[26px]">
              <div className="flex items-center gap-2.5">
                <span className="text-teal">
                  <Wordmark size={26} />
                </span>
                <span className="bg-teal-tint text-teal-dark font-body rounded-pill px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.1em]">
                  {t('admin.badge')}
                </span>
                {env.APP_ENV === 'staging' ? (
                  <span className="bg-warn-bg text-warn-text font-body rounded-pill px-2.5 py-1 text-[11px] font-bold uppercase tracking-[0.1em]">
                    {t('admin.shell.staging')}
                  </span>
                ) : null}
              </div>

              <div className="flex items-center gap-[18px]">
                {items.map((item) =>
                  item.menu ? (
                    <NavDropdown key={item.to} label={item.label} entries={item.menu} />
                  ) : (
                    <NavLink key={item.to} to={item.to} className={navClass}>
                      {item.label}
                      {item.count ? <Counter value={item.count} /> : null}
                      {item.dot ? <StatusDot tone={item.dot} /> : null}
                    </NavLink>
                  ),
                )}
              </div>
            </div>

            <div className="flex items-center gap-3">
              {can('enquiries.manage') ? (
                <NavLink
                  to="/aufgaben"
                  className={({ isActive }) =>
                    `border-line text-ink font-body rounded-buttonSm inline-flex h-9 items-center gap-2 border bg-white px-3 text-sm font-semibold ${
                      isActive ? 'border-lime' : ''
                    }`
                  }
                >
                  <CheckIcon />
                  {t('admin.nav.tasks')}
                  {nav?.tasksOpen ? <Counter value={nav.tasksOpen} /> : null}
                </NavLink>
              ) : null}

              {/* § 3 — one operator location group in v1, so this lists one
                  entry and is read-only. Kept for later partner operators. */}
              <p className="border-line text-muted font-body rounded-buttonSm inline-flex h-9 items-center gap-2 border bg-white px-3 text-sm">
                <PinIcon />
                {t('admin.shell.location')}
              </p>

              <AvatarMenu />
            </div>
          </nav>
        </header>

        <Outlet />
      </div>
      <Toaster />
    </div>
  );
}

const navClass = ({ isActive }: { isActive: boolean }) =>
  `font-body inline-flex items-center gap-1.5 whitespace-nowrap text-[15px] font-semibold ${
    isActive ? 'text-ink border-lime border-b-2 pb-0.5' : 'text-muted hover:text-ink'
  }`;

interface MenuEntry {
  to: string;
  label: string;
}

/** "Finanzen ▾" (§ 3). Opens on click, closes on pick, `Esc` or a click away. */
function NavDropdown({ label, entries }: { label: string; entries: MenuEntry[] }) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const active = useLocation().pathname;
  const isActive = entries.some((entry) => active.startsWith(entry.to));

  useEffect(() => {
    if (!open) return;
    const onPointer = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (entries.length === 0) return null;

  return (
    <div ref={wrapper} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-haspopup="menu"
        className={navClass({ isActive })}
      >
        {label}
        <Chevron />
      </button>
      {open ? (
        <div
          role="menu"
          className="border-line rounded-input absolute left-0 top-9 z-50 w-[200px] border bg-white p-1.5 shadow-lg"
        >
          {entries.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              role="menuitem"
              onClick={() => setOpen(false)}
              className="font-body text-ink hover:bg-fog rounded-segmentInner flex h-[38px] items-center px-2.5 text-sm"
            >
              {entry.label}
            </NavLink>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="14"
      height="14"
      aria-hidden
      className="block shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function Counter({ value }: { value: number }) {
  return (
    <span className="bg-teal-tint text-teal-dark font-body rounded-pill inline-flex h-[18px] min-w-[18px] items-center justify-center px-1.5 text-[11px] font-bold tabular-nums">
      {value}
    </span>
  );
}

function StatusDot({ tone }: { tone: 'ok' | 'warn' }) {
  const { t } = useTranslation();
  return (
    <span
      className={`rounded-pill h-2 w-2 shrink-0 ${tone === 'ok' ? 'bg-success-text' : 'bg-warn-dot'}`}
      role="img"
      aria-label={t(tone === 'ok' ? 'admin.shell.allOnline' : 'admin.shell.someOffline')}
    />
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden
      className="text-teal-dark block shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <path d="m9 11 3 3L22 4" />
    </svg>
  );
}

function PinIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="16"
      height="16"
      aria-hidden
      className="text-teal block shrink-0"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
      <path d="M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
    </svg>
  );
}
